import React, { useEffect, useRef, useMemo } from 'react'

/**
 * Renders a Google AdSense banner for the public home page.
 * Only mounts when `config.enabled`, `config.client`, and `config.slot` are all truthy.
 */
const HomeAdSenseBanner = ({ config, className = '' }) => {
  const adRef = useRef(null)
  const key = `${config?.client}-${config?.slot}-${config?.testMode ? 'test' : 'live'}`

  const shouldRender = useMemo(
    () => !!(config?.enabled && config?.client && config?.slot),
    [config],
  )

  useEffect(() => {
    if (!shouldRender || !adRef.current) return

    // Ensure the AdSense script is loaded
    const src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config.client}`
    if (!document.querySelector(`script[src="${src}"]`)) {
      const script = document.createElement('script')
      script.async = true
      script.src = src
      script.crossOrigin = 'anonymous'
      document.head.appendChild(script)
    }

    // Push ad after a brief delay to allow the script to initialise
    const timer = setTimeout(() => {
      try {
        if (adRef.current && adRef.current.dataset.adsbygoogleStatus !== 'done') {
          ;(window.adsbygoogle = window.adsbygoogle || []).push({})
        }
      } catch (_) {
        /* ignore duplicate push errors */
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [shouldRender, config?.client, config?.slot])

  if (!shouldRender) return null

  return (
    <div className={`w-full bg-gray-100 py-2 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ins
          key={key}
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', textAlign: 'center' }}
          data-ad-client={config.client}
          data-ad-slot={config.slot}
          data-ad-format="auto"
          data-full-width-responsive="true"
          data-adtest={config.testMode ? 'on' : 'off'}
        />
      </div>
    </div>
  )
}

export default HomeAdSenseBanner
